import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { getPost, posts } from '@/content/blog';
import { renderArticle, postUrl } from '@/lib/blogFormat';
import { CALENDLY_URL } from '@/lib/site';
import NotFound from './NotFound';

/**
 * The article markup comes from renderArticle(), the same function the
 * prerenderer uses to write it into the static HTML. Crawlers and visitors get
 * identical content. The source is our own markdown, escaped by the renderer.
 */
const BlogPost = () => {
  const { id } = useParams();
  const post = getPost(id || '');

  if (!post) return <NotFound />;

  const more = posts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-white to-slate-100 dark:from-slate-900 dark:to-slate-950">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={postUrl(post)} />
      </Helmet>
      <div className="container-custom py-12 max-w-3xl mx-auto">
        <article
          className="blog-article prose prose-lg prose-slate dark:prose-invert max-w-none prose-headings:scroll-mt-24 prose-a:text-primary"
          dangerouslySetInnerHTML={{ __html: renderArticle(post) }}
        />

        <div className="mt-12 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <h2 className="text-2xl font-bold">Want a second opinion on your setup?</h2>
          <p className="mt-2 text-slate-600 dark:text-slate-300">
            Book a free 30-minute call. We'll look at what you have and tell you honestly what we'd change.
          </p>
          <Button asChild size="lg" className="mt-6 rounded-full">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">Book a free call</a>
          </Button>
        </div>

        {more.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold mb-4">More from the blog</h2>
            <ul className="space-y-3">
              {more.map((p) => (
                <li key={p.slug}>
                  <Link to={`/blog/${p.slug}`} className="text-primary font-semibold hover:underline">
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
